import React, { useMemo } from 'react'
import { Box, Card, Flex, SimpleGrid, Text, VStack, Skeleton, SkeletonText } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { fetchRoleCounts } from '../../api/workforce/workforce'
import { UserGroupIcon, PlaceholderIcon, DotsHorizontal16Icon } from '../../assets/icons'
import { useTranslation } from 'react-i18next'
import { useRole } from '../../hooks/auth/useRole'

const AccessRolesTable: React.FC = () => {
  const { t } = useTranslation()
  const { isSuperAdmin } = useRole()
  const navigate = useNavigate()

  const ALL_ROLE_LABELS: Record<string, string> = {
    super_admin: t('settings.access.roles.super_admin'),
    org_admin: t('settings.access.roles.org_admin'),
    finance: t('settings.access.roles.finance'),
    country_admin: t('settings.access.roles.country_admin'),
    team_leader: t('settings.access.roles.team_leader'),
    user: t('common.fields.user'),
  }

  const ROLE_LABELS = useMemo(() => {
    if (isSuperAdmin) return ALL_ROLE_LABELS
    const { super_admin, ...rest } = ALL_ROLE_LABELS
    return rest
  }, [isSuperAdmin, t])

  const roleDescriptions: Record<string, string> = {
    super_admin: t('settings.access.roleDescriptions.super_admin'),
    org_admin: t('settings.access.roleDescriptions.org_admin'),
    finance: t('settings.access.roleDescriptions.finance'),
    country_admin: t('settings.access.roleDescriptions.country_admin'),
    team_leader: t('settings.access.roleDescriptions.team_leader'),
    user: t('settings.access.roleDescriptions.user'),
  }

  const { data: roleCounts, isFetching } = useQuery({
    queryKey: ['role-counts'],
    queryFn: fetchRoleCounts,
    staleTime: 30000,
  })

  const roleKeys = Object.keys(ROLE_LABELS)

  const roles = roleKeys.map((role) => ({
    title: role,
    countDisplay: roleCounts?.[role] || 0,
  }))

  const handleRowClick = (role: { title: string; countDisplay: number }) => {
    navigate(`/user/settings/access/roles/${role.title}`, {
      state: {
        roleName: role.title,
        roleDisplayName: ROLE_LABELS[role.title] || role.title,
      }
    })
  }

  const RoleCard: React.FC<{ role: { title: string; countDisplay: number }; onClick: (role: { title: string; countDisplay: number }) => void }> = ({ role, onClick }) => (
    <Box
      borderWidth="1px"
      p={4}
      bg="white"
      boxShadow="sm"
      _hover={{ boxShadow: 'md', cursor: 'pointer' }}
      onClick={() => onClick(role)}
      position="relative"
    >
      <Flex align="flex-start" mb={2}>
        <Box
          borderRadius="md"
          bg="teal.50"
          boxSize="48px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr={3}
        >
          <PlaceholderIcon boxSize={8} color="teal.400" />
        </Box>
        <VStack align="start" spacing={0} flex="1">
          <Text textStyle="h-s">
            {ROLE_LABELS[role.title] || role.title}
          </Text>
          <Text fontStyle="b-xs">
            <UserGroupIcon /> {t('settings.access.labels.peopleCount', { count: role.countDisplay })}
          </Text>
        </VStack>
        <Box ml="auto" alignSelf="flex-start">
          <DotsHorizontal16Icon boxSize={6} color="solid.400" />
        </Box>
      </Flex>
      <Text textStyle="l-s">
        {roleDescriptions[role.title] || t('common.labels.notAvailable')}
      </Text>
    </Box>
  )

  const RoleCardSkeleton: React.FC = () => (
    <Box
      borderWidth="1px"
      p={4}
      bg="white"
      boxShadow="sm"
      position="relative"
    >
      <Flex align="flex-start" mb={2}>
        <Skeleton
          borderRadius="md"
          boxSize="48px"
          mr={3}
        />
        <VStack align="start" spacing={0} flex="1">
          <Skeleton height="20px" width="120px" mb={2} />
          <Skeleton height="16px" width="80px" />
        </VStack>
        <Skeleton boxSize={6} />
      </Flex>
      <SkeletonText mt={2} noOfLines={2} spacing="4" skeletonHeight="2" />
    </Box>
  )

  return (
    <Card width="100%" p="0">
      <SimpleGrid
        templateColumns="repeat(3, 1fr)"
        gap="16px"
        p="24px"
      >
        {isFetching ? (
          Array.from({ length: 6 }).map((_, index) => (
            <RoleCardSkeleton key={index} />
          ))
        ) : (
          roles.map((role) => (
            <RoleCard key={role.title} role={role} onClick={handleRowClick} />
          ))
        )}
      </SimpleGrid>
    </Card>
  )
}

export default AccessRolesTable
